/**
 * Random helpers backed by the Web Crypto API.
 *
 * Nothing here is security-critical — this app picks mock products and mints
 * demo document numbers. `Math.random()` would be functionally fine, but Sonar
 * raises `typescript:S2245` (a Critical vulnerability) on every call site, and
 * five of those were enough to hold the project's security rating at D and block
 * the pipeline. A CSPRNG costs nothing here and removes the question entirely,
 * which is a better answer than sprinkling NOSONAR across the codebase.
 *
 * `crypto.getRandomValues` is available in every browser the build targets
 * (chrome111 / safari16.4, see vite.config.ts) and in Node 22.
 */

/**
 * A uniformly distributed integer in `[0, max)`. Returns 0 when `max` <= 0 so
 * callers indexing an empty array get a harmless value rather than NaN.
 *
 * Uses rejection sampling: taking `value % max` directly would bias the low end
 * whenever `max` does not divide 2^32 evenly.
 */
export function randomInt(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 0
  const bound = Math.floor(max)
  const limit = Math.floor(0xff_ff_ff_ff / bound) * bound
  const buf = new Uint32Array(1)
  let value: number
  do {
    crypto.getRandomValues(buf)
    value = buf[0] ?? 0
  } while (value >= limit)
  return value % bound
}

/** A random element of `items`, or undefined when empty. */
export function randomItem<T>(items: readonly T[]): T | undefined {
  if (items.length === 0) return undefined
  return items[randomInt(items.length)]
}

/**
 * An integer in `[min, max]`, inclusive of both ends — the shape most callers
 * here want ("a delivery 1 to 5 days out").
 */
export function randomIntBetween(min: number, max: number): number {
  if (max < min) return min
  return min + randomInt(max - min + 1)
}

/**
 * A short opaque id. Time-prefixed so ids sort roughly by creation, with random
 * suffix for uniqueness within the same millisecond.
 */
export function randomId(): string {
  const buf = new Uint32Array(2)
  crypto.getRandomValues(buf)
  const suffix = Array.from(buf, (n) => n.toString(36)).join('')
  return `${Date.now().toString(36)}-${suffix}`
}
